import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import PasswordForm from '../../components/PasswordForm/PasswordForm';
import BackButton from '../../components/BackButton/BackButton';
import Dropdown from '../../components/Dropdown/Dropdown';
import { sportsOptions } from '../../components/SportSelect/sportsOptions';
import { GET_INVITE_INFO } from '../../apollo/queries/user';
import useRegister from '../../hooks/auth/useRegister';
import useRequestPasswordReset from '../../hooks/auth/useRequestPasswordReset';
import useResetPassword from '../../hooks/auth/useResetPassword';
import styles from './PasswordPage.module.css';

const PasswordPage = ({ type }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedSport, setSelectedSport] = useState('');
  const { token: paramToken } = useParams();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const queryToken = searchParams.get('token');

  const token = queryToken || paramToken;

  const { data: inviteInfoData, loading: inviteInfoLoading, error: inviteInfoError } = useQuery(GET_INVITE_INFO, {
    variables: { token },
    skip: type !== 'athlete-setup' || !token,
  });
  const isExistingUser = inviteInfoData?.getInviteInfo?.isExistingUser ?? false;

  const { registerWithInvite } = useRegister();
  const { requestPasswordReset } = useRequestPasswordReset();
  const { resetPassword } = useResetPassword();

  const handleSubmit = async (formData) => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (type === 'coach-setup' || type === 'athlete-setup') {
        if (!token) throw new Error("Missing invite token");
        if (formData.password !== formData.confirmPassword) {
          throw new Error("Passwords do not match");
        }
        if (type === 'athlete-setup' && !isExistingUser) {
          if (!firstName.trim() || !lastName.trim()) {
            throw new Error('Please enter your first and last name.');
          }
          if (!selectedSport) {
            throw new Error('Please select your sport to continue.');
          }
        }

        await registerWithInvite({
          token,
          password: formData.password,
          ...(type === 'athlete-setup' && !isExistingUser && {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            sports: [selectedSport]
          })
        });
      
        setSuccessMessage(isExistingUser
          ? "Invitation accepted! Redirecting to login..."
          : "Your account has been created! Redirecting to login..."
        );
        setTimeout(() => {
          navigate('/login/select-role');
        }, 2000);
      }
      else if (type === 'forgot') {
        const result = await requestPasswordReset({ email: formData.email });
        if (result) {
          setSuccessMessage('A reset link has been sent to your email.');
        }
      }

      else if (type === 'reset') {
        if (!token) throw new Error("Missing reset token");
        if (formData.password !== formData.confirmPassword) {
          throw new Error("Passwords do not match");
        }
  
        const result = await resetPassword({ token, password: formData.password });
  
        if (result) {
          setSuccessMessage('Your password has been successfully reset! Redirecting to login...');
          setTimeout(() => {
            navigate('/login/select-role');
          }, 2000);
        }
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const pageConfig = {
    forgot: {
      heading: 'Forgot Password',
      subtext: 'Enter your email below and we’ll send you a password reset link.'
    },
    reset: {
      heading: 'Reset Password',
      subtext: 'Enter your new password below to reset your account.'
    },
    'coach-setup': {
      heading: 'Create Your Coach Account',
      subtext: 'Set your password to complete registration.'
    },
    'athlete-setup': {
      heading: isExistingUser ? 'Accept Coach Invitation' : 'Create Your Athlete Account',
      subtext: isExistingUser
        ? 'You already have an account. Enter your password to accept this invitation.'
        : 'Enter your name, select your sport, and set your password to complete your registration.'
    }
  };

  const { heading, subtext } = pageConfig[type] || {};

  if (type === 'athlete-setup' && inviteInfoError) {
    return (
      <div className={styles.wrapper}>
        <BackButton />
        <h2 className={styles.heading}>Invitation Expired</h2>
        <p className={styles.subtext}>
          This invitation link has expired or is no longer valid. Please ask your coach to send a new invite.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <BackButton />
      <h2 className={styles.heading}>{heading}</h2>
      <p className={styles.subtext}>{subtext}</p>

      {type === 'athlete-setup' && !isExistingUser && !inviteInfoLoading && (
        <>
          <div className={styles.nameFields}>
            <input
              className={styles.nameInput}
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <input
              className={styles.nameInput}
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <Dropdown
            options={sportsOptions
              .filter((opt) => !opt.isComingSoon)
              .map((opt) => ({ label: opt.label, value: opt.value }))}
            value={selectedSport}
            onChange={setSelectedSport}
            placeholder="Select your sport..."
          />
        </>
      )}

      <PasswordForm
        type={type}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
      />

      {successMessage && <p className={styles.success}>{successMessage}</p>}
    </div>
  );
};

export default PasswordPage;